import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientMarketFixConnectionsQueryResponse, GetAllClientMarketFixConnectionsPathParams, GetAllClientMarketFixConnectionsQueryParams } from "../types/GetAllClientMarketFixConnections";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientMarketFixConnectionsClient = typeof client<GetAllClientMarketFixConnectionsQueryResponse, never, never>;
type GetAllClientMarketFixConnections = {
    data: GetAllClientMarketFixConnectionsQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientMarketFixConnectionsPathParams;
    queryParams: GetAllClientMarketFixConnectionsQueryParams;
    headerParams: never;
    response: GetAllClientMarketFixConnectionsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientMarketFixConnectionsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientMarketFixConnectionsClient>>;
    };
};
export const getAllClientMarketFixConnectionsQueryKey = (clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/connections/fix", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketFixConnectionsQueryKey = ReturnType<typeof getAllClientMarketFixConnectionsQueryKey>;
export function getAllClientMarketFixConnectionsQueryOptions(clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"], options: GetAllClientMarketFixConnections["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketFixConnectionsQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketFixConnections["data"], GetAllClientMarketFixConnections["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/connections/fix`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all FIX connections for a clients market
 * @summary Get all FIX connections
 * @link /api/clients/:clientCode/markets/:marketCode/connections/fix
 */
export function useGetAllClientMarketFixConnections<TData = GetAllClientMarketFixConnections["response"], TQueryData = GetAllClientMarketFixConnections["response"], TQueryKey extends QueryKey = GetAllClientMarketFixConnectionsQueryKey>(clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientMarketFixConnections["response"], GetAllClientMarketFixConnections["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientMarketFixConnections["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientMarketFixConnections["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketFixConnectionsQueryKey(clientCode, marketCode, params);
    const query = useQuery({
        ...getAllClientMarketFixConnectionsQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientMarketFixConnections["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientMarketFixConnectionsSuspenseQueryKey = (clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/connections/fix", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketFixConnectionsSuspenseQueryKey = ReturnType<typeof getAllClientMarketFixConnectionsSuspenseQueryKey>;
export function getAllClientMarketFixConnectionsSuspenseQueryOptions(clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"], options: GetAllClientMarketFixConnections["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketFixConnectionsSuspenseQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketFixConnections["data"], GetAllClientMarketFixConnections["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/connections/fix`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all FIX connections for a clients market
 * @summary Get all FIX connections
 * @link /api/clients/:clientCode/markets/:marketCode/connections/fix
 */
export function useGetAllClientMarketFixConnectionsSuspense<TData = GetAllClientMarketFixConnections["response"], TQueryKey extends QueryKey = GetAllClientMarketFixConnectionsSuspenseQueryKey>(clientCode: GetAllClientMarketFixConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketFixConnectionsPathParams["marketCode"], params?: GetAllClientMarketFixConnections["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientMarketFixConnections["response"], GetAllClientMarketFixConnections["error"], TData, TQueryKey>>;
    client?: GetAllClientMarketFixConnections["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientMarketFixConnections["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketFixConnectionsSuspenseQueryKey(clientCode, marketCode, params);
    const query = useSuspenseQuery({
        ...getAllClientMarketFixConnectionsSuspenseQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientMarketFixConnections["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}